import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Modal,
  Dropdown,
  ButtonGroup
} from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import Icon from '../../common/Icons';
import receptionService from '../../../services/receptionService';
import CompanyService from '../../../services/companyService';
import externalMemberService from '../../../services/externalMemberService';
import rendezVousService from '../../../services/rendezVousService';
import { ROLES } from '../../../constants/roles';
import { useAuth } from '../../../context/AuthContext';
import { ORIENT_LABELS, ORIENT_LABELS_SHORT, ORIENT_SERVICE, MEMBER_TYPES } from '../receptionConstants';
import { dispatchNurseQueueRefresh } from '../../nurse/utils/nurseQueueEvents';
import { formatVisitDate, formatVisitTime, visitStatusLabel } from '../receptionFormatters';
import {
  combineYmdAndTime,
  formatDateTimeForDb,
  parseToInstant
} from '../../../utils/appTimezone';
import ReceptionOrientedConsultationsPanel from '../panels/ReceptionOrientedConsultationsPanel';
import HouseholdRegistrationModal from '../forms/HouseholdRegistrationModal';
import RendezVousFormModal from '../forms/RendezVousFormModal';
import IntakeOrientModal from '../forms/IntakeOrientModal';
import ExternalMemberForm from '../panels/ExternalMemberForm';
import { formatMemberAge, formatMemberSexeShort, formatPersonProfileMeta, memberDisplayName } from '../browse/browseFormatters';
import {
  ReceptionPageHeader,
  ReceptionLoadingBlock,
  ReceptionEmptyState,
  ReceptionStatusBadge,
  ReceptionSearchAnchor,
  ReceptionFloatingResults,
  ReceptionFloatingResultItem
} from '../ui';

export default function ReceptionDashboardPage() {
  const { user } = useAuth();
  const { toast, defaultOrientation, activeReceptionSite } = useOutletContext();

  const [memberSearch, setMemberSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [externalMatches, setExternalMatches] = useState([]);
  const [searchError, setSearchError] = useState('');
  const [searchResultsOpen, setSearchResultsOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [recentActivitiesLoading, setRecentActivitiesLoading] = useState(false);
  const [recentActivitiesMessage, setRecentActivitiesMessage] = useState('');

  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(true);

  const [orientationStaff, setOrientationStaff] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [orientationStaffLoading, setOrientationStaffLoading] = useState(false);
  const [showHouseholdModal, setShowHouseholdModal] = useState(false);
  const [showExternalMemberModal, setShowExternalMemberModal] = useState(false);
  const [externalMemberSaving, setExternalMemberSaving] = useState(false);
  const [externalMemberForm, setExternalMemberForm] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    sexe: '',
    telephone: '',
    email: '',
    adresse: ''
  });

  const [pendingIntakeVisit, setPendingIntakeVisit] = useState(null);

  const [showOrientModal, setShowOrientModal] = useState(false);
  const [orientDoctorId, setOrientDoctorId] = useState('');
  const [orientSymptoms, setOrientSymptoms] = useState('');
  const [orientSiteId, setOrientSiteId] = useState('');
  const [orientSubmitting, setOrientSubmitting] = useState(false);
  const [doctorFilter, setDoctorFilter] = useState('');
  const [orientServiceLine, setOrientServiceLine] = useState('DENTAL');

  const [orientedListReload, setOrientedListReload] = useState(0);
  const [showRdvModal, setShowRdvModal] = useState(false);
  const [rdvSubmitting, setRdvSubmitting] = useState(false);
  const [rdvDoctors, setRdvDoctors] = useState([]);
  const [rdvPatientSearch, setRdvPatientSearch] = useState('');
  const [rdvPatientLoading, setRdvPatientLoading] = useState(false);
  const [rdvPatientOptions, setRdvPatientOptions] = useState([]);
  const [rdvAvailability, setRdvAvailability] = useState(null);
  const [rdvForm, setRdvForm] = useState({
    type_rendez_vous: 'MEDECIN',
    doctor_preference: 'SANS_PREFERENCE',
    doctor_id: '',
    patient_kind: 'SMIE',
    patient_id: '',
    service: 'CONSULTATION_MEDECIN',
    date_rdv: '',
    heure_debut: '',
    date_fin: '',
    heure_fin: '',
    motif: '',
    priority: 'NORMALE',
    note_interne: ''
  });

  const canUseReceptionApi =
    user?.role === ROLES.RECEPTIONIST ||
    user?.role === ROLES.ADMIN ||
    user?.role === ROLES.ADMIN_PERSONNEL;

  const activeReceptionSiteId = activeReceptionSite?.id != null
    ? String(activeReceptionSite.id)
    : '';

  const visitListParams = useMemo(() => ({ status: 'ORIENTED' }), []);

  const loadVisits = useCallback(async () => {
    if (!canUseReceptionApi) return;
    setVisitsLoading(true);
    try {
      const res = await receptionService.getVisits(visitListParams);
      if (res.success) setVisits(res.data || []);
    } catch (e) {
      console.error(e);
      toast('danger', e.message || 'Impossible de charger les visites');
    } finally {
      setVisitsLoading(false);
    }
  }, [canUseReceptionApi, toast, visitListParams]);

  const loadMeta = useCallback(async () => {
    if (!canUseReceptionApi) return;
    try {
      const companyRes = await CompanyService.getActiveCompanies();
      if (companyRes.success) setCompanies(companyRes.data || []);
    } catch (e) {
      console.error(e);
      toast('warning', 'Liste des sociétés : ' + (e.message || ''));
    }
  }, [canUseReceptionApi, toast]);

  useEffect(() => {
    loadVisits();
    loadMeta();
  }, [loadVisits, loadMeta]);

  useEffect(() => {
    const loadRdvDoctors = async () => {
      try {
        const doctorRes = await rendezVousService.doctors();
        if (doctorRes.success) setRdvDoctors(Array.isArray(doctorRes.data) ? doctorRes.data : []);
      } catch (_) {
        // non bloquant pour l'accueil
      }
    };
    loadRdvDoctors();
  }, []);

  const getPatientDisplayName = useCallback((kind, member) => {
    const name = `${member?.prenom || ''} ${member?.nom || ''}`.trim();
    return name || member?.nom || 'Patient';
  }, []);

  const loadPatientRecentActivities = useCallback(async (kind, member) => {
    if (kind === 'SMIE' && member?.matricule) {
      setRecentActivitiesLoading(true);
      setRecentActivitiesMessage('');
      try {
        const historyRes = await receptionService.getVisitHistoryByMatricule(member.matricule, { limit: 5 });
        const rows = historyRes.success && Array.isArray(historyRes.data) ? historyRes.data : [];
        setRecentActivities(rows);
        setRecentActivitiesMessage(rows.length === 0 ? 'Aucun passage récent pour ce patient.' : '');
      } catch (historyError) {
        setRecentActivities([]);
        setRecentActivitiesMessage(historyError.message || 'Historique récent indisponible.');
      } finally {
        setRecentActivitiesLoading(false);
      }
      return;
    }

    setRecentActivities([]);
    setRecentActivitiesLoading(false);
    setRecentActivitiesMessage(
      kind === 'EXTERNAL'
        ? 'Historique automatique disponible pour les adhérents avec matricule.'
        : 'Historique indisponible sans matricule.'
    );
  }, []);

  const selectPatient = useCallback(
    async (kind, member) => {
      setSelectedPatient({ kind, member });
      setSearchResultsOpen(false);
      setSearchError('');
      setMemberSearch(getPatientDisplayName(kind, member));
      await loadPatientRecentActivities(kind, member);
    },
    [getPatientDisplayName, loadPatientRecentActivities]
  );

  const resetPatientSelection = useCallback(() => {
    setSelectedPatient(null);
    setRecentActivities([]);
    setRecentActivitiesMessage('');
    setRecentActivitiesLoading(false);
  }, []);

  const changeSelectedPatient = useCallback(() => {
    resetPatientSelection();
    setMemberSearch('');
    setMatches([]);
    setExternalMatches([]);
    setSearchResultsOpen(false);
    setSearchError('');
    setSearchLoading(false);
  }, [resetPatientSelection]);

  const performPatientSearch = useCallback(async (rawQuery, { notifyEmpty = false } = {}) => {
    setSearchError('');
    const q = rawQuery.trim();
    if (q.length < 2) {
      if (notifyEmpty) {
        setSearchError('Saisissez au moins 2 caractères (matricule, nom ou prénom)');
      }
      setSearchResultsOpen(false);
      setMatches([]);
      setExternalMatches([]);
      return;
    }
    setSearchLoading(true);
    setSearchResultsOpen(true);
    try {
      const res = await receptionService.searchMembers(q, { limit: 30 });
      const extRes = await externalMemberService.list({ search: q, limit: 30, offset: 0 });
      const list = res.success ? res.data || [] : [];
      const extList = extRes.success ? extRes.data || [] : [];
      setMatches(list);
      setExternalMatches(extList);
      if (notifyEmpty && list.length === 0 && extList.length === 0) {
        toast('warning', 'Aucun membre ne correspond à cette recherche');
      }
    } catch (err) {
      setSearchError(err.message || 'Erreur de recherche');
      setMatches([]);
      setExternalMatches([]);
    } finally {
      setSearchLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    const q = memberSearch.trim();
    if (q.length < 2) {
      setSearchResultsOpen(false);
      setMatches([]);
      setExternalMatches([]);
      setSearchLoading(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      performPatientSearch(q);
    }, 320);

    return () => window.clearTimeout(timer);
  }, [memberSearch, performPatientSearch]);

  const handleSearch = async (e) => {
    e.preventDefault();
    await performPatientSearch(memberSearch, { notifyEmpty: true });
  };

  const clearPatientSearch = () => {
    changeSelectedPatient();
  };

  const filteredOrientationStaff = useMemo(() => {
    const needle = doctorFilter.trim().toLowerCase();
    if (!needle) return orientationStaff;
    return orientationStaff.filter((d) => {
      const hay = [d.name, d.email, d.first_name, d.last_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [orientationStaff, doctorFilter]);

  useEffect(() => {
    if (!showOrientModal || !orientDoctorId) return;
    const ok = filteredOrientationStaff.some((d) => String(d.id) === String(orientDoctorId));
    if (!ok) setOrientDoctorId('');
  }, [showOrientModal, orientDoctorId, filteredOrientationStaff]);

  const loadOrientationStaffForModal = useCallback(async () => {
    if (!showOrientModal || !canUseReceptionApi) return;
    setOrientationStaffLoading(true);
    try {
      const res = await receptionService.getOrientationStaff(orientServiceLine);
      if (res.success) setOrientationStaff(Array.isArray(res.data) ? res.data : []);
      else setOrientationStaff([]);
    } catch (e) {
      console.error(e);
      toast('danger', e.message || 'Liste professionnels indisponible');
      setOrientationStaff([]);
    } finally {
      setOrientationStaffLoading(false);
    }
  }, [showOrientModal, canUseReceptionApi, orientServiceLine, toast]);

  useEffect(() => {
    loadOrientationStaffForModal();
  }, [loadOrientationStaffForModal]);

  const closeOrientModal = () => {
    setShowOrientModal(false);
    setPendingIntakeVisit(null);
  };

  const openIntakeOrientModal = ({ memberId = null, externalMemberId = null }) => {
    if (!activeReceptionSite?.id) {
      toast('warning', "Choisissez d'abord le site d'accueil.");
      return;
    }
    setPendingIntakeVisit({ memberId, externalMemberId });
    setOrientServiceLine(defaultOrientation);
    setOrientDoctorId('');
    setOrientSymptoms('');
    setOrientSiteId(activeReceptionSiteId);
    setDoctorFilter('');
    setShowOrientModal(true);
  };

  const registerSelectedPatientVisit = () => {
    if (!selectedPatient) return;
    if (selectedPatient.kind === 'SMIE') {
      openIntakeOrientModal({ memberId: selectedPatient.member.id });
    } else {
      openIntakeOrientModal({ externalMemberId: selectedPatient.member.id });
    }
  };

  const openRdvForSelectedPatient = () => {
    if (selectedPatient) {
      const label = getPatientDisplayName(selectedPatient.kind, selectedPatient.member);
      setRdvForm((prev) => ({
        ...prev,
        patient_kind: selectedPatient.kind === 'EXTERNAL' ? 'EXTERNE' : 'SMIE',
        patient_id: String(selectedPatient.member.id)
      }));
      setRdvPatientSearch(label);
      setRdvPatientOptions([]);
    }
    setShowRdvModal(true);
  };

  const resetExternalMemberForm = () => {
    setExternalMemberForm({
      nom: '',
      prenom: '',
      date_naissance: '',
      sexe: '',
      telephone: '',
      email: '',
      adresse: ''
    });
  };

  const submitExternalMember = async (e) => {
    e.preventDefault();
    setExternalMemberSaving(true);
    try {
      const payload = {
        nom: externalMemberForm.nom.trim(),
        prenom: externalMemberForm.prenom.trim() || null,
        date_naissance: externalMemberForm.date_naissance || null,
        sexe: externalMemberForm.sexe || null,
        telephone: externalMemberForm.telephone.trim() || null,
        email: externalMemberForm.email.trim() || null,
        adresse: externalMemberForm.adresse.trim() || null
      };
      const res = await externalMemberService.create(payload);
      if (res.success) {
        toast('success', 'Membre externe créé avec succès.');
        setShowExternalMemberModal(false);
        resetExternalMemberForm();
      }
    } catch (err) {
      toast('danger', err.message || 'Création membre externe impossible');
    } finally {
      setExternalMemberSaving(false);
    }
  };

  const submitVisitWithOrient = async () => {
    if (!pendingIntakeVisit) return;

    const resolvedSite =
      orientSiteId && String(orientSiteId).trim() !== ''
        ? parseInt(orientSiteId, 10)
        : activeReceptionSiteId
          ? parseInt(activeReceptionSiteId, 10)
          : null;
    if (!resolvedSite || Number.isNaN(resolvedSite)) {
      toast('warning', "Choisissez d'abord le site d'accueil.");
      return;
    }

    setOrientSubmitting(true);
    try {
      const payload = {
        ...(pendingIntakeVisit.memberId ? { member_id: pendingIntakeVisit.memberId } : {}),
        ...(pendingIntakeVisit.externalMemberId
          ? { external_member_id: pendingIntakeVisit.externalMemberId }
          : {}),
        site_id: resolvedSite,
        service_line: orientServiceLine,
        symptoms: orientSymptoms.trim() || undefined,
        notes: orientSymptoms.trim() || undefined,
        ...(orientDoctorId ? { doctor_id: parseInt(orientDoctorId, 10) } : {})
      };
      const res = await receptionService.registerAndOrientVisit(payload);
      if (!res.success) {
        throw new Error(res.message || 'Enregistrement impossible');
      }

      closeOrientModal();
      toast('success', 'Passage enregistré et patient orienté.');
      if (orientServiceLine === ORIENT_SERVICE.NURSING) {
        dispatchNurseQueueRefresh(resolvedSite);
      }
      await loadVisits();
      setOrientedListReload((n) => n + 1);
    } catch (err) {
      toast('danger', err.message || 'Enregistrement impossible');
    } finally {
      setOrientSubmitting(false);
    }
  };

  const combineDateAndTime = (datePart, timePart) => combineYmdAndTime(datePart, timePart);

  const visibleVisits = useMemo(() => {
    if (!activeReceptionSiteId) {
      return visits.filter((visit) => visit.status === 'ORIENTED');
    }
    return visits.filter(
      (visit) =>
        visit.status === 'ORIENTED' && String(visit.site_id || '') === activeReceptionSiteId
    );
  }, [activeReceptionSiteId, visits]);

  const queuePatientTypeLabel = useCallback((visit) => {
    if (!visit.member_id) return 'Externe';
    return MEMBER_TYPES[visit.member_type] || visit.member_type || 'Adhérent';
  }, []);

  const queueServiceLineClass = useCallback((line) => {
    const key = String(line || '').toUpperCase();
    if (key === ORIENT_SERVICE.DENTAL) return 'recv-service-line--dental';
    if (key === ORIENT_SERVICE.NURSING) return 'recv-service-line--nursing';
    return 'recv-service-line--medical';
  }, []);

  const queueServiceLineLabel = useCallback((line) => {
    if (!line) return null;
    return ORIENT_LABELS_SHORT[line] || ORIENT_LABELS[line] || line;
  }, []);

  const selectedPatientProfile = useMemo(() => {
    if (!selectedPatient) return null;
    const { kind, member } = selectedPatient;
    const displayName = getPatientDisplayName(kind, member);
    const typeLabel =
      kind === 'EXTERNAL'
        ? member.statut || 'Membre externe'
        : MEMBER_TYPES[member.type] || member.type || 'Adhérent SMIE';
    const details = [
      member.matricule ? `Matricule ${member.matricule}` : null,
      member.company_name || null,
      typeLabel,
      member.telephone || null,
      formatMemberSexeShort(member.sexe) !== '—' ? formatMemberSexeShort(member.sexe) : null,
      formatMemberAge(member.date_naissance) !== '—' ? formatMemberAge(member.date_naissance) : null
    ].filter(Boolean);

    return { displayName, typeLabel, details };
  }, [getPatientDisplayName, selectedPatient]);

  const visitQueueCount = visibleVisits.length;
  const patientSearchOpen = searchResultsOpen && memberSearch.trim().length >= 2;

  useEffect(() => {
    const q = rdvPatientSearch.trim();
    if (q.length < 2) {
      setRdvPatientOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setRdvPatientLoading(true);
      try {
        if (rdvForm.patient_kind === 'SMIE') {
          const res = await receptionService.searchMembers(q, { limit: 10 });
          const opts = (res.data || []).map((m) => ({
            id: m.id,
            label: `${m.prenom || ''} ${m.nom || ''}`.trim(),
            sub: m.type === 'AGENT' ? 'SMIE' : 'AYANT_DROIT'
          }));
          setRdvPatientOptions(opts);
        } else {
          const res = await externalMemberService.list({ search: q, limit: 10, offset: 0 });
          const opts = (res.data || []).map((m) => ({
            id: m.id,
            label: `${m.prenom || ''} ${m.nom || ''}`.trim(),
            sub: 'EXTERNE'
          }));
          setRdvPatientOptions(opts);
        }
      } catch (e) {
        setRdvPatientOptions([]);
      } finally {
        setRdvPatientLoading(false);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [rdvPatientSearch, rdvForm.patient_kind]);

  useEffect(() => {
    const needsPractitioner =
      (rdvForm.type_rendez_vous === 'MEDECIN' || rdvForm.type_rendez_vous === 'DENTISTE') &&
      rdvForm.doctor_preference === 'SPECIFIQUE';
    if (!needsPractitioner || !rdvForm.doctor_id || !rdvForm.date_rdv || !rdvForm.heure_debut) {
      setRdvAvailability(null);
      return;
    }
    const start = combineDateAndTime(rdvForm.date_rdv, rdvForm.heure_debut);
    if (!start) return;
    const endCandidate = combineDateAndTime(rdvForm.date_fin, rdvForm.heure_fin);
    const startInstant = parseToInstant(start);
    const end =
      endCandidate ||
      formatDateTimeForDb(new Date(startInstant.getTime() + 30 * 60000));
    const timer = setTimeout(async () => {
      try {
        const res = await rendezVousService.availability({
          doctor_id: rdvForm.doctor_id,
          start_at: start,
          end_at: end
        });
        if (res.success) setRdvAvailability(res.data || null);
      } catch (e) {
        setRdvAvailability({ available: false, conflicts: [] });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [
    rdvForm.type_rendez_vous,
    rdvForm.doctor_preference,
    rdvForm.doctor_id,
    rdvForm.date_rdv,
    rdvForm.heure_debut,
    rdvForm.date_fin,
    rdvForm.heure_fin
  ]);

  const handleRdvPatientSearchChange = useCallback((value, patientId) => {
    setRdvPatientSearch(value);
    if (patientId !== undefined) {
      setRdvForm((prev) => ({ ...prev, patient_id: patientId }));
      setRdvPatientOptions([]);
    } else {
      setRdvForm((prev) => ({ ...prev, patient_id: '' }));
    }
  }, []);

  const submitRdv = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const start = combineDateAndTime(rdvForm.date_rdv, rdvForm.heure_debut);
    if (!start) {
      toast('warning', 'Date/heure de début invalide');
      return;
    }
    const endCandidate = combineDateAndTime(rdvForm.date_fin, rdvForm.heure_fin);
    if ((rdvForm.date_fin && !rdvForm.heure_fin) || (!rdvForm.date_fin && rdvForm.heure_fin)) {
      toast('warning', 'Renseignez date_fin et heure_fin ensemble, ou laissez les deux vides.');
      return;
    }
    if (endCandidate && parseToInstant(endCandidate) <= parseToInstant(start)) {
      toast('warning', 'heure_fin doit être après heure_debut');
      return;
    }

    const withPractitioner =
      (rdvForm.type_rendez_vous === 'MEDECIN' || rdvForm.type_rendez_vous === 'DENTISTE') &&
      rdvForm.doctor_preference === 'SPECIFIQUE';
    if (withPractitioner && !rdvForm.doctor_id) {
      toast('warning', 'Médecin / dentiste requis pour "avec praticien spécifique"');
      return;
    }
    if (!rdvForm.patient_id) {
      toast('warning', 'Sélectionnez un patient');
      return;
    }

    setRdvSubmitting(true);
    try {
      const payload = {
        type_rendez_vous: rdvForm.type_rendez_vous,
        service: rdvForm.service,
        priority: rdvForm.priority,
        doctor_preference:
          rdvForm.type_rendez_vous === 'MEDECIN' || rdvForm.type_rendez_vous === 'DENTISTE'
            ? rdvForm.doctor_preference
            : 'SANS_PREFERENCE',
        ...(withPractitioner ? { doctor_id: parseInt(rdvForm.doctor_id, 10) } : {}),
        start_at: start,
        ...(endCandidate ? { end_at: endCandidate } : {}),
        motif: rdvForm.motif.trim() || 'Consultation',
        note_interne: rdvForm.note_interne.trim() || null,
        ...(rdvForm.patient_kind === 'SMIE'
          ? { member_id: parseInt(rdvForm.patient_id, 10) }
          : { external_member_id: parseInt(rdvForm.patient_id, 10) })
      };
      const res = await rendezVousService.create(payload);
      if (res.success) {
        toast('success', 'Rendez-vous créé avec succès.');
        setShowRdvModal(false);
        setRdvPatientSearch('');
        setRdvPatientOptions([]);
        setRdvForm((prev) => ({
          ...prev,
          doctor_id: '',
          patient_id: '',
          date_rdv: '',
          heure_debut: '',
          date_fin: '',
          heure_fin: '',
          motif: '',
          note_interne: '',
          priority: 'NORMALE'
        }));
      }
    } catch (err) {
      toast('danger', err.message || 'Création RDV impossible');
    } finally {
      setRdvSubmitting(false);
    }
  };

  if (!canUseReceptionApi) {
    return null;
  }

  return (
    <div className="recv-dash-pro recv-dash-pro--main">
      <ReceptionPageHeader
        badge="Accueil"
        title="Tableau de bord"
        description="Identifiez le patient, enregistrez le passage et suivez la file du jour en priorité."
        actions={
          <Button variant="primary" onClick={() => setShowRdvModal(true)}>
            + Nouveau RDV
          </Button>
        }
      />

      <section className="recv-dash-primary recv-dash-primary--ops recv-no-print mb-4" aria-label="Accueil du jour">
        <div className="recv-dash-primary__head recv-dash-primary__head--ops mb-4">
          <div>
            <span className="recv-dash-primary__badge">Poste d&apos;accueil</span>
            <h2 className="recv-dash-primary__title mb-1">Accueil du jour</h2>
            <p className="recv-dash-primary__desc mb-0">
              {activeReceptionSite?.name ? (
                <>
                  Site <strong>{activeReceptionSite.name}</strong> — identifiez le patient puis enregistrez le passage.
                </>
              ) : (
                'Identifiez le patient, enregistrez son passage et orientez-le en une seule étape.'
              )}
            </p>
          </div>
        </div>
        <Row className="g-4 recv-dash-twocol">
        <Col xs={12} xl={6} className={patientSearchOpen ? 'recv-intake-col--open' : undefined}>
          <Card
            className={`border-0 recv-dash-card h-100 recv-dash-card--elevated recv-dash-card--primary recv-intake-card${
              patientSearchOpen ? ' is-search-open' : ''
            }`}
          >
            <Card.Header className="recv-dash-card__head recv-intake-card__head border-0 bg-transparent mb-0">
              <span className="recv-dash-card__eyebrow">Identification</span>
              <Card.Title className="h5 mb-0 fw-semibold mt-2 text-body">
                Trouver le patient
              </Card.Title>
              <Card.Subtitle className="text-muted small mt-2 mb-0 fw-normal">
                Matricule, nom, prénom ou téléphone. Sélectionnez un patient pour afficher sa fiche avant d&apos;enregistrer le passage.
              </Card.Subtitle>
            </Card.Header>
            <Card.Body className="recv-intake-card__body">
              <Form onSubmit={handleSearch} className="recv-intake-form">
                <div className="recv-intake-form__topline">
                  <Form.Label className="recv-intake-form__label mb-0">Recherche patient</Form.Label>
                  {memberSearch.trim().length >= 2 && (
                    <span className="recv-intake-form__count">
                      {matches.length + externalMatches.length} résultat(s)
                    </span>
                  )}
                </div>
                <div className="recv-intake-search-row">
                  <ReceptionSearchAnchor className="recv-intake-search">
                    <span className="recv-intake-search__icon" aria-hidden>
                      <Icon name="search" size={18} />
                    </span>
                      <Form.Control
                        className="recv-intake-search__input"
                        value={memberSearch}
                        onChange={(ev) => {
                          const v = ev.target.value;
                          if (
                            selectedPatient &&
                            v.trim() !== getPatientDisplayName(selectedPatient.kind, selectedPatient.member)
                          ) {
                            resetPatientSelection();
                            setMatches([]);
                            setExternalMatches([]);
                          }
                          setMemberSearch(v);
                          if (v.trim().length < 2) {
                            setSearchResultsOpen(false);
                            setSearchError('');
                          } else {
                            setSearchResultsOpen(true);
                          }
                        }}
                        placeholder="Matricule, nom, prénom ou téléphone..."
                        autoComplete="off"
                        aria-controls="dash-member-search-list"
                        aria-expanded={searchResultsOpen}
                      />
                      {memberSearch ? (
                        <button
                          type="button"
                          className="recv-intake-search__clear"
                          onClick={clearPatientSearch}
                          aria-label="Effacer la recherche patient"
                        >
                          <Icon name="times" size={14} />
                        </button>
                      ) : null}
                      <ReceptionFloatingResults
                        listId="dash-member-search-list"
                        show={patientSearchOpen}
                        loading={searchLoading}
                        emptyMessage="Aucun résultat pour cette recherche"
                      >
                        {matches.length > 0 && (
                          <>
                            <div className="recv-floating-results__title">Adhérents SMIE</div>
                            {matches.map((m) => (
                              <ReceptionFloatingResultItem
                                key={m.id}
                                primary={memberDisplayName(m)}
                                secondary={m.company_name || undefined}
                                meta={formatPersonProfileMeta({
                                  matricule: m.matricule,
                                  typeLabel: MEMBER_TYPES[m.type] || m.type,
                                  sexe: m.sexe,
                                  date_naissance: m.date_naissance
                                })}
                                actionLabel="Sélectionner"
                                selected={
                                  selectedPatient?.kind === 'SMIE' &&
                                  String(selectedPatient.member.id) === String(m.id)
                                }
                                onSelect={() => selectPatient('SMIE', m)}
                              />
                            ))}
                          </>
                        )}
                        {externalMatches.length > 0 && (
                          <>
                            <div className="recv-floating-results__title">Membres externes</div>
                            {externalMatches.map((m) => (
                              <ReceptionFloatingResultItem
                                key={`ext-${m.id}`}
                                primary={memberDisplayName(m) || m.nom}
                                meta={formatPersonProfileMeta({
                                  leading: [m.telephone, m.statut || 'EXTERNE'].filter(Boolean),
                                  sexe: m.sexe,
                                  date_naissance: m.date_naissance
                                })}
                                actionLabel="Sélectionner"
                                selected={
                                  selectedPatient?.kind === 'EXTERNAL' &&
                                  String(selectedPatient.member.id) === String(m.id)
                                }
                                onSelect={() => selectPatient('EXTERNAL', m)}
                              />
                            ))}
                          </>
                        )}
                      </ReceptionFloatingResults>
                  </ReceptionSearchAnchor>
                  <Button type="submit" variant="primary" className="recv-intake-search__submit" disabled={searchLoading}>
                    {searchLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        Recherche
                      </>
                    ) : (
                      'Rechercher'
                    )}
                  </Button>
                </div>
                <div className="recv-intake-search__hint">
                  {selectedPatient
                    ? 'Patient identifié. Vérifiez la fiche ci-dessous puis enregistrez le passage avec orientation ou planifiez un RDV.'
                    : 'La recherche se lance automatiquement. Sélectionnez un résultat pour afficher la fiche patient.'}
                </div>
                {searchError && <div className="text-danger small mt-2">{searchError}</div>}
                {selectedPatient && selectedPatientProfile ? (
                  <div className="recv-patient-card" aria-live="polite">
                    <div className="recv-patient-card__head">
                      <div className="recv-patient-card__identity">
                        <span className="recv-patient-card__badge">Patient identifié</span>
                        <h3 className="recv-patient-card__name">{selectedPatientProfile.displayName}</h3>
                        <p className="recv-patient-card__details mb-0">
                          {selectedPatientProfile.details.join(' · ')}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="recv-patient-card__change"
                        onClick={changeSelectedPatient}
                      >
                        Changer
                      </button>
                    </div>

                    <div className="recv-recent-activity recv-patient-card__history">
                      <div className="recv-recent-activity__head">
                        <div>
                          <span className="recv-recent-activity__eyebrow">Dernières activités</span>
                          <h4 className="recv-recent-activity__title mb-0">5 derniers passages</h4>
                        </div>
                        {recentActivities.length > 0 && (
                          <span className="recv-recent-activity__count">
                            {recentActivities.length}/5
                          </span>
                        )}
                      </div>
                      {recentActivitiesLoading ? (
                        <div className="recv-recent-activity__loading">
                          <Spinner animation="border" size="sm" />
                          <span>Chargement des derniers passages…</span>
                        </div>
                      ) : recentActivities.length > 0 ? (
                        <div className="recv-recent-activity__list">
                          {recentActivities.map((activity) => (
                            <article key={activity.id} className="recv-recent-activity__item">
                              <div className="recv-recent-activity__date">
                                {formatVisitDate(activity.registered_at)}
                              </div>
                              <div className="recv-recent-activity__body">
                                <div className="recv-recent-activity__main">
                                  {activity.service_line
                                    ? ORIENT_LABELS[activity.service_line] || activity.service_line
                                    : 'Passage réception'}
                                </div>
                                <div className="recv-recent-activity__meta">
                                  {[activity.company_name, activity.visit_site_name, activity.registered_by_name]
                                    .filter(Boolean)
                                    .join(' · ') || 'Détails non renseignés'}
                                </div>
                              </div>
                              <span className={`recv-recent-activity__status ${activity.status === 'ORIENTED' ? 'is-ok' : 'is-pending'}`}>
                                {visitStatusLabel(activity.status)}
                              </span>
                            </article>
                          ))}
                        </div>
                      ) : (
                        <p className="recv-recent-activity__empty mb-0">
                          {recentActivitiesMessage || 'Aucune activité récente à afficher.'}
                        </p>
                      )}
                    </div>

                    <div className="recv-patient-card__actions">
                      <Button variant="primary" onClick={registerSelectedPatientVisit}>
                        Enregistrer et orienter
                      </Button>
                      <Button variant="outline-primary" onClick={openRdvForSelectedPatient}>
                        <Icon name="calendar" size={14} />
                        Nouveau RDV
                      </Button>
                    </div>
                  </div>
                ) : null}
                <div className="recv-intake-actions" aria-label="Actions rapides">
                  <Dropdown as={ButtonGroup} className="recv-intake-actions__group">
                    <Button variant="outline-success" onClick={() => setShowHouseholdModal(true)}>
                      <Icon name="plus" size={14} />
                      Nouveau foyer
                    </Button>
                    <Dropdown.Toggle split variant="outline-success" id="dashboard-create-menu" />
                    <Dropdown.Menu align="end">
                      <Dropdown.Item onClick={() => setShowHouseholdModal(true)}>
                        Ajouter un foyer (ayants droit)
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => setShowExternalMemberModal(true)}>
                        Ajouter un membre externe (rapide)
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} xl={6}>
          <Card className="border-0 recv-dash-card h-100 recv-dash-card--elevated recv-dash-card--primary recv-queue-card">
            <Card.Header className="recv-dash-card__head recv-queue-card__head border-0 bg-transparent mb-0">
              <div>
                <span className="recv-dash-card__eyebrow">File du jour</span>
                <Card.Title className="h5 mb-0 fw-semibold mt-2 text-body">Passages orientés</Card.Title>
                <Card.Subtitle className="text-muted small mt-2 mb-0 fw-normal">
                  Passages enregistrés et orientés depuis ce site aujourd&apos;hui.
                  {activeReceptionSite?.name ? (
                    <span className="recv-queue-card__site"> · {activeReceptionSite.name}</span>
                  ) : null}
                </Card.Subtitle>
              </div>
              <div className="recv-queue-card__toolbar">
                <span className="recv-queue-card__ok">
                  {visitQueueCount} passage{visitQueueCount > 1 ? 's' : ''}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="recv-queue-card__refresh"
                  onClick={loadVisits}
                  disabled={visitsLoading}
                  aria-label="Actualiser la file du jour"
                >
                  {visitsLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <Icon name="refresh" size={14} aria-hidden />
                      Actualiser
                    </>
                  )}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {visitsLoading ? (
                <ReceptionLoadingBlock label="Chargement de la file d'accueil…" />
              ) : visibleVisits.length === 0 ? (
                <div className="px-3 py-2">
                  <ReceptionEmptyState
                    title="File vide pour ce site"
                    description={`Aucun passage orienté aujourd'hui pour ${activeReceptionSite?.name || 'ce site'}. Identifiez un patient pour enregistrer son passage.`}
                  />
                </div>
              ) : (
                <>
                <div className="recv-queue-list-wrap">
                  <div className="recv-queue-list" role="list" aria-label="Passages orientés du jour">
                    <div className="recv-queue-list__head" aria-hidden="true">
                      <span className="recv-queue-list__cell recv-queue-list__cell--patient">Patient</span>
                      <span className="recv-queue-list__cell recv-queue-list__cell--line">Filière</span>
                      <span className="recv-queue-list__cell recv-queue-list__cell--consult">Consultation</span>
                    </div>
                    {visibleVisits.map((v) => {
                      const patientName = `${v.member_prenom || ''} ${v.member_nom || ''}`.trim() || 'Patient';
                      const patientMeta = [v.member_matricule, queuePatientTypeLabel(v)].filter(Boolean).join(' · ');
                      const staffName = v.registered_by_name || '—';

                      return (
                        <article key={v.id} className="recv-queue-row" role="listitem">
                          <div className="recv-queue-row__patient recv-queue-list__cell recv-queue-list__cell--patient">
                            <span className="recv-queue-row__time font-monospace">{formatVisitTime(v.registered_at)}</span>
                            <div className="recv-queue-row__name" title={patientName}>
                              {patientName}
                            </div>
                            {patientMeta ? (
                              <div className="recv-queue-row__meta" title={patientMeta}>
                                {patientMeta}
                              </div>
                            ) : null}
                            {v.company_name ? (
                              <div className="recv-queue-row__company" title={v.company_name}>
                                {v.company_name}
                              </div>
                            ) : null}
                          </div>
                          <div className="recv-queue-row__line recv-queue-list__cell recv-queue-list__cell--line">
                            {v.service_line ? (
                              <span
                                className={`recv-service-line recv-service-line--compact ${queueServiceLineClass(v.service_line)}`}
                                title={ORIENT_LABELS[v.service_line] || v.service_line}
                              >
                                {queueServiceLineLabel(v.service_line)}
                              </span>
                            ) : (
                              <span className="recv-queue-row__empty">—</span>
                            )}
                          </div>
                          <div className="recv-queue-row__consult recv-queue-list__cell recv-queue-list__cell--consult">
                            <div className="recv-queue-row__consult-main">
                              <span className="recv-queue-consult__id font-monospace">
                                {v.consultation_id ? `#C${v.consultation_id}` : '—'}
                              </span>
                              {v.consultation_status ? (
                                <ReceptionStatusBadge kind="consultation" value={v.consultation_status} />
                              ) : null}
                            </div>
                            <span className="recv-queue-row__staff" title={staffName}>
                              {staffName}
                            </span>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
                <div className="recv-queue-table__foot">
                  {visibleVisits.length} passage{visibleVisits.length > 1 ? 's orientés' : ' orienté'} aujourd&apos;hui
                  {activeReceptionSite?.name ? ` · ${activeReceptionSite.name}` : ''}
                </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
        </Row>
      </section>

      <Row className="g-4 mb-4">
        <Col xs={12}>
          <ReceptionOrientedConsultationsPanel
            toast={toast}
            reloadSignal={orientedListReload}
            activeReceptionSite={activeReceptionSite}
          />
        </Col>
      </Row>

      <RendezVousFormModal
        show={showRdvModal}
        onHide={() => setShowRdvModal(false)}
        saving={rdvSubmitting}
        onSubmit={submitRdv}
        form={rdvForm}
        setForm={setRdvForm}
        patientSearch={rdvPatientSearch}
        onPatientSearchChange={handleRdvPatientSearchChange}
        patientOptions={rdvPatientOptions}
        patientLoading={rdvPatientLoading}
        doctors={rdvDoctors}
        availability={rdvAvailability}
      />

      <IntakeOrientModal
        show={showOrientModal}
        onHide={closeOrientModal}
        saving={orientSubmitting}
        onSubmit={submitVisitWithOrient}
        patientProfile={selectedPatientProfile}
        activeReceptionSite={activeReceptionSite}
        defaultOrientation={defaultOrientation}
        serviceLine={orientServiceLine}
        onServiceLineChange={setOrientServiceLine}
        doctorId={orientDoctorId}
        onDoctorIdChange={setOrientDoctorId}
        doctorFilter={doctorFilter}
        onDoctorFilterChange={setDoctorFilter}
        orientationStaff={orientationStaff}
        orientationStaffLoading={orientationStaffLoading}
        symptoms={orientSymptoms}
        onSymptomsChange={setOrientSymptoms}
      />

      <HouseholdRegistrationModal
        show={showHouseholdModal}
        onHide={() => setShowHouseholdModal(false)}
        companies={companies}
        toast={toast}
        onCreated={() => {
          setShowHouseholdModal(false);
          setMemberSearch('');
          setMatches([]);
          setExternalMatches([]);
        }}
      />

      <Modal
        show={showExternalMemberModal}
        onHide={() => {
          if (externalMemberSaving) return;
          setShowExternalMemberModal(false);
          resetExternalMemberForm();
        }}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Nouveau membre externe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ExternalMemberForm
            value={externalMemberForm}
            onChange={setExternalMemberForm}
            onSubmit={submitExternalMember}
            onCancel={() => {
              if (externalMemberSaving) return;
              setShowExternalMemberModal(false);
              resetExternalMemberForm();
            }}
            saving={externalMemberSaving}
            editing={false}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
